import React, { memo } from "react";
import classNames from "classnames";
import {
  AppButton,
  AppButtonProps,
} from "@campgladiator/cgui-core.atoms.app-button";
import { Icon } from "@campgladiator/cgui-core.atoms.icon";
import { Logo } from "@campgladiator/cgui-core.atoms.logo";
import { Label, Paragraph } from "@campgladiator/cgui-core.atoms.typography";
import { setHrefTarget } from "@campgladiator/cgui-core.utilities.helpers.links";
import { breakpoints } from "@campgladiator/cgui-core.utilities.helpers.resolution-variables";
import { useDeviceWidth } from "@campgladiator/cgui-core.utilities.hooks.use-device-width";
import * as grid from "@campgladiator/cgui-core.styles.helpers.grid";
import {
  logoNav,
  workoutProgramNav,
  trainersNav,
  companyNav,
  socialList,
  appDownloads,
  helpNav,
  shopNav,
  footnoteLinks,
  nutritionNav,
  SocialLink,
  FooterLink,
} from "./footerData";
import styles from "./footer.module.scss";

export type DownloadLink = {
  name: string;
} & AppButtonProps;

export type FooterProps = {
  className?: string;
  children?: never;
} & React.HTMLAttributes<HTMLDivElement>;

type NavItemProps = {
  nav: FooterLink;
};

const NavItem = ({ nav }: NavItemProps) => (
  <li className={styles.item}>
    <a className={styles.link} href={nav.link} target={setHrefTarget(nav.link)}>
      <Paragraph className={styles.text} size="xsmall" weight="book">
        {nav.text}
      </Paragraph>
    </a>
  </li>
);

type NavColumnProps = {
  title: string;
  links: FooterLink[];
};

const NavColumn = ({ title, links }: NavColumnProps) => (
  <div className={styles.navList}>
    <nav className={styles.nav}>
      <Paragraph size="xsmall" className={styles.columnHeader}>
        {title}
      </Paragraph>
      <ul className={styles.list}>
        {links.map((nav: FooterLink) => (
          <NavItem nav={nav} key={nav.link + nav.text} />
        ))}
      </ul>
    </nav>
  </div>
);

type SocialProps = {
  items: SocialLink[];
};

const Social = ({ items }: SocialProps) => (
  <div className={styles.followDownload}>
    <div className={styles.social}>
      <Paragraph size="xsmall" className={styles.columnHeader}>
        Follow
      </Paragraph>
      <ul className={styles.list}>
        {items.map(({ link, title, icon }) => (
          <li className={styles.item} key={icon + link}>
            <a
              href={link}
              target="_blank"
              title={title}
              className={styles.link}
            >
              <Icon.Line name={icon} className={styles.icon} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

type DownloadsProps = { downloads: DownloadLink[] };

const Downloads = ({ downloads }: DownloadsProps) => (
  <div className={styles.followDownload}>
    <div className={styles.downloads}>
      <Paragraph size="xsmall" className={styles.columnHeader}>
        Download
      </Paragraph>
      <div className={styles.buttons}>
        {downloads.map(({ name, brand, size, theme }) => (
          <AppButton
            key={name + brand}
            title={name}
            brand={brand}
            size={size}
            theme={theme}
          />
        ))}
      </div>
    </div>
  </div>
);

const Footnote = () => {
  const year = new Date().getFullYear();
  return (
    <>
      <div className={grid.col12}>
        <div className={classNames(styles.footnote, grid.col12)}></div>
      </div>
      <div className={grid.colLg2}></div>
      <div
        className={classNames(
          styles.termsPrivacy,
          grid.col12,
          grid.colMd6,
          grid.colLg5
        )}
      >
        {footnoteLinks.map(({ link, text }: FooterLink) => (
          <a
            href={link}
            target={setHrefTarget(link)}
            className={styles.link}
            key={link + text}
          >
            <Label size="xsmall">{text}</Label>
          </a>
        ))}
      </div>
      <div
        className={classNames(
          styles.copyright,
          grid.col12,
          grid.colMd6,
          grid.colLg5
        )}
      >
        <Label size="xsmall" className={styles.text}>
          © {year} Camp Gladiator. All rights reserved.
        </Label>
      </div>
    </>
  );
};

const FooterLogo = () => (
  <div className={styles.logoWrapper}>
    <a href={logoNav.link} target={setHrefTarget(logoNav.link)}>
      <Logo type="icon" iconType="white" width="50px" className={styles.logo} />
    </a>
  </div>
);

const WorkoutProgram = () => (
  <NavColumn title="Workout Program" links={workoutProgramNav} />
);
const Nutrition = () => <NavColumn title="Nutrition" links={nutritionNav} />;
const Trainers = () => <NavColumn title="Trainers" links={trainersNav} />;
const Company = () => <NavColumn title="Company" links={companyNav} />;
const Shop = () => <NavColumn title="Shop" links={shopNav} />;
const Help = () => <NavColumn title="Help" links={helpNav} />;

const LargeFooterLists = () => (
  <>
    <div className={grid.colLg2}>
      <FooterLogo />
    </div>
    <div className={grid.colLg2}>
      <WorkoutProgram />
    </div>
    <div className={grid.colLg2}>
      <Nutrition />
    </div>
    <div className={grid.colLg2}>
      <Trainers />
    </div>
    <div className={grid.colLg2}>
      <Company />
    </div>
    <div className={grid.colLg2}>
      <Shop />
      <Help />
    </div>
  </>
);

const FooterListsMedium = () => (
  <>
    <div className={grid.col2}>
      <FooterLogo />
    </div>
    <div className={grid.col3}>
      <WorkoutProgram />
    </div>
    <div className={grid.col3}>
      <Nutrition />
    </div>
    <div className={grid.col3}>
      <Trainers />
    </div>
    <div className={grid.col2}>{/* spacer */}</div>
    <div className={grid.col3}>
      <Company />
    </div>
    <div className={grid.col3}>
      <Shop />
    </div>
    <div className={grid.col3}>
      <Help />
    </div>
  </>
);

const FooterListsSmall = () => (
  <>
    <div className={grid.col12}>
      <FooterLogo />
    </div>
    <div className={grid.col6}>
      <WorkoutProgram />
    </div>
    <div className={grid.col6}>
      <Nutrition />
      <Trainers />
    </div>
    <div className={grid.col6}>
      <Company />
    </div>
    <div className={grid.col6}>
      <Shop />
      <Help />
    </div>
  </>
);

const FooterLists = () => {
  const deviceWidth = useDeviceWidth();
  if (deviceWidth > breakpoints.tabletLarge) return <LargeFooterLists />;
  if (deviceWidth > breakpoints.tabletMedium) return <FooterListsMedium />;
  return <FooterListsSmall />;
};

const Footer = ({ className = "", ...props }: FooterProps) => (
  <footer className={classNames(styles.footer, className)} {...props}>
    <div className={grid.container}>
      <div className={grid.row}>
        <FooterLists />
      </div>
      <div className={grid.row}>
        <div className={grid.col2}></div>
        <div className={classNames(grid.col12, grid.colMd4, grid.colLg5)}>
          <Social items={socialList} />
        </div>
        <div className={classNames(grid.col12, grid.colMd6, grid.colLg5)}>
          <Downloads downloads={appDownloads} />
        </div>
      </div>
      <div className={grid.row}>
        <Footnote />
      </div>
    </div>
  </footer>
);

export default memo(Footer);
